import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import UserService from '../../services/userService';
import { showMsg } from '../../services/library';
import InvGroupService from '../../services/invGroupService';
import AuthService from '../../services/authService';
import { STORAGE_DIRECTLOGIN } from '../../constants/storageConst';
import TeamService from '../../services/teamService';
import DealerService from '../../services/dealerService';
import InventoryService from '../../services/inventoryService';
import ImportService from '../../services/importerService';
import { TEAM } from '../../constants/messageConst';

const REQ_ACCOUNT = 'accounts/';

export const fetchUsersReq = createAsyncThunk(
  `${REQ_ACCOUNT}fetchUsersReq`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getUsers();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchInvGroupsReq = createAsyncThunk(
  `${REQ_ACCOUNT}fetchInvGroupsReq`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await InvGroupService.getInvGroups();
      if (response?.status) {
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchSaveGroup = createAsyncThunk(
  `${REQ_ACCOUNT}fetchSaveGroup`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InvGroupService.saveInvGroup(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchReset2FA = createAsyncThunk(
  `${REQ_ACCOUNT}fetchReset2FA`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AuthService.reset2FA(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchResetPass = createAsyncThunk(
  `${REQ_ACCOUNT}fetchResetPass`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AuthService.accountReset(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchReInvite = createAsyncThunk(
  `${REQ_ACCOUNT}fetchReInvite`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await UserService.sendReInvite(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchDirectLoginById = createAsyncThunk(
  `${REQ_ACCOUNT}fetchDirectLoginById`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AuthService.directLoginById(payload);
      if (response?.status) {
        const obj = response.data ? response.data[0] : null;
        if (obj) {
          const str = encodeURIComponent(JSON.stringify(obj));
          return str;
        }
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchChangePassword = createAsyncThunk(
  `${REQ_ACCOUNT}fetchChangePassword`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await UserService.changeUserPassword(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAddTeam = createAsyncThunk(
  `${REQ_ACCOUNT}fetchAddTeam`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await UserService.addTeam(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchUserActiveDeactivate = createAsyncThunk(
  `${REQ_ACCOUNT}fetchUserActiveDeactivate`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await UserService.userActiveDeactive(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchEditTeam = createAsyncThunk(
  `${REQ_ACCOUNT}fetchEditTeam`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await TeamService.editTeamProfile(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchChangeUserType = createAsyncThunk(
  `${REQ_ACCOUNT}fetchChangeUserType`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await TeamService.changeUserType(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchProfile = createAsyncThunk(
  `${REQ_ACCOUNT}fetchProfile`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DealerService.getProfile(payload);
      if (response?.status) {
        // showMsg(response.message, 'success');
        return response.data;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAddEditDealership = createAsyncThunk(
  `${REQ_ACCOUNT}fetchAddEditDealership`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DealerService.addNewDealer(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchEditRoleForDealership = createAsyncThunk(
  `${REQ_ACCOUNT}fetchEditRoleForDealership`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DealerService.dealersRoles(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchSendPhotoGroupReport = createAsyncThunk(
  `${REQ_ACCOUNT}fetchSendPhotoGroupReport`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.sendGroupReport(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchSendVDSGroupReport = createAsyncThunk(
  `${REQ_ACCOUNT}fetchSendVDSGroupReport`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.sendVDSGroupReport(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAgeBucketReport = createAsyncThunk(
  `${REQ_ACCOUNT}fetchAgeBucketReport`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InventoryService.sendAgeBucketReport(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchImportGroupReport = createAsyncThunk(
  `${REQ_ACCOUNT}fetchImportGroupReport`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ImportService.sendImportGroupReport(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchDeleteGroup = createAsyncThunk(
  `${REQ_ACCOUNT}fetchDeleteGroup`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InvGroupService.deleteInvGroup(payload);
      if (response?.status) {
        showMsg(response.message, 'success');
        return { ...payload, ...response };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const accountsSlice = createSlice({
  name: 'accounts',
  initialState: {
    userList: [],
    tableLoaded: true,
    iGroupData: [],
    iGroupLoaded: true,
    iGroupFilter: [],
    accountFilterData: [],
    dealerProfileData: {},
    editProfileToggle: false,
    teamToggle: false,
    editTeamToggle: false,
    dealerShipToggle: false,
    editRoleToggle: false,
    changePasswordToggle: false,
    editRoleModalForDealer: false,
    userPassData: {},
    teamIdForEdit: '',
    dealershipIdForEdit: '',
    teamIdForRoleEdit: '',
    actionGroup: '',
    isGroupEditing: false,
    editGroupId: 0,
    listFilter: TEAM,
    activeClass: TEAM,
    defaultDate: moment().toDate(),
    fromDate: moment().toDate(),
    userRolesOption: [],
    userRolesSelection: null,
    directLoginModalToggle: false
  },
  reducers: {
    setUserRolesOption: {
      reducer(state, action) {
        state.userRolesSelection = null;
        state.userRolesOption = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setUserRolesSelection: {
      reducer(state, action) {
        state.userRolesSelection = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setUserList: {
      reducer(state, action) {
        state.userList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setDealerShipToggle: {
      reducer(state, action) {
        state.dealerShipToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setFromDate: {
      reducer(state, action) {
        state.fromDate = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setActionGroup: {
      reducer(state, action) {
        state.actionGroup = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setEditGroupId: {
      reducer(state, action) {
        state.editGroupId = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setTeamIdForEdit: {
      reducer(state, action) {
        state.teamIdForEdit = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setIsGroupEditing: {
      reducer(state, action) {
        state.isGroupEditing = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setDealershipIdForEdit: {
      reducer(state, action) {
        state.dealershipIdForEdit = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setTeamIdForRoleEdit: {
      reducer(state, action) {
        state.teamIdForRoleEdit = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },

    setActiveClass: {
      reducer(state, action) {
        state.activeClass = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setListFilter: {
      reducer(state, action) {
        state.listFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setEditRoleModalForDealer: {
      reducer(state, action) {
        state.editRoleModalForDealer = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setUserPassData: {
      reducer(state, action) {
        state.userPassData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setEditTeamToggle: {
      reducer(state, action) {
        state.editTeamToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setEditRoleToggle: {
      reducer(state, action) {
        state.editRoleToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setChangePasswordToggle: {
      reducer(state, action) {
        state.changePasswordToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setTeamToggle: {
      reducer(state, action) {
        state.teamToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setTableLoaded: {
      reducer(state, action) {
        state.tableLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },

    setIGroupData: {
      reducer(state, action) {
        state.iGroupData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setIGroupLoaded: {
      reducer(state, action) {
        state.iGroupLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setEditProfileToggle: {
      reducer(state, action) {
        state.editProfileToggle = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setDealerProfileData: {
      reducer(state, action) {
        state.dealerProfileData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setIGroupFilter: {
      reducer(state, action) {
        state.iGroupFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setAccountFilterData: {
      reducer(state, action) {
        state.accountFilterData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setDirectLoginModalToggle: {
      reducer(state, action) {
        state.directLoginModalToggle = action.payload;
        if (!action.payload) {
          localStorage.removeItem(STORAGE_DIRECTLOGIN);
        }
      },
      prepare(e) {
        return { payload: e };
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUsersReq.pending, state => {
        state.userList = [];
        state.tableLoaded = true;
      })
      .addCase(fetchUsersReq.fulfilled, (state, action) => {
        const data = action.payload;
        state.userList = data;
        state.tableLoaded = false;
      })
      .addCase(fetchUsersReq.rejected, state => {
        state.tableLoaded = false;
      })

      .addCase(fetchInvGroupsReq.pending, state => {
        state.iGroupData = [];
        state.iGroupFilter = [];
        state.iGroupLoaded = true;
      })
      .addCase(fetchInvGroupsReq.fulfilled, (state, action) => {
        const data = action.payload;
        if (data && data.length && data.length > 0) {
          state.iGroupData = data;
          state.iGroupFilter = data;
        } else {
          state.iGroupData = [];
          state.iGroupFilter = [];
        }
        state.iGroupLoaded = false;
      })
      .addCase(fetchInvGroupsReq.rejected, state => {
        state.iGroupLoaded = false;
      })

      .addCase(fetchUserActiveDeactivate.fulfilled, (state, action) => {
        const item = action.payload;
        const data = state.userList.map(x => {
          return {
            ...x,
            user_isactive:
              x.user_id === item.user_id
                ? x.user_isactive == 0 || x.user_isactive == false
                  ? 1
                  : 0
                : x.user_isactive
          };
        });
        state.userList = data;
      })
      .addCase(fetchProfile.pending, state => {
        state.dealerProfileData = {};
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        const data = action.payload;
        state.dealerProfileData = data?.[0]?.dealer || {};
        state.editProfileToggle = !state.editProfileToggle;
      })

      .addCase(fetchDirectLoginById.fulfilled, (state, action) => {
        const str = action.payload;
        state.directLoginModalToggle = true;
        localStorage.setItem(STORAGE_DIRECTLOGIN, str);
      })
      .addCase(fetchDirectLoginById.rejected, state => {
        state.directLoginModalToggle = false;
        localStorage.removeItem(STORAGE_DIRECTLOGIN);
      });
  }
});

export const {
  setUserList,
  setTableLoaded,
  setIGroupLoaded,
  setIGroupData,
  setIGroupFilter,
  setAccountFilterData,
  setDirectLoginModalToggle,
  setEditProfileToggle,
  setDealerShipToggle,
  setTeamToggle,
  setEditTeamToggle,
  setEditRoleToggle,
  setChangePasswordToggle,
  setEditRoleModalForDealer,
  setListFilter,
  setUserPassData,
  setActiveClass,
  setTeamIdForEdit,
  setDealershipIdForEdit,
  setTeamIdForRoleEdit,
  setEditGroupId,
  setIsGroupEditing,
  setFromDate,
  setActionGroup,
  setUserRolesOption,
  setUserRolesSelection
} = accountsSlice.actions;
export default accountsSlice.reducer;
